'use client';

import { clsx } from 'clsx';
import { startTransition, useRef, useContext, Suspense } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';

// TODO после удалении разбора папки intl использовать обычный импорт из атомов, это временное решение конфликта circular dependency
import { IconButton } from 'libs/atoms/src/lib/components/button/IconButton';
import { Icons } from 'libs/atoms/src/lib/components/icons';

import { langTitleDict, localesList } from '@monoimin/constants';

import { getLocalePrefix } from '../utils';

import { AlternatePageContext } from './AlternatePageProvider';

import { removeLocaleFromPathname } from './utils';

import type { Locales } from '@monoimin/sb-types';

interface AlternatePageSwitcherProps {
  locale: Locales;
  classNames?: {
    container?: string;
    title?: string;
    options?: string;
    globeIcon?: string;
    arrowIcon?: string;
  };
}

const AlternatePageSwitcherItem = ({
  item,
  locale,
}: {
  item: (typeof localesList)[number];
  locale: Locales;
}) => {
  const [state] = useContext(AlternatePageContext);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const pathname = usePathname();

  const router = useRouter();

  const searchParams = useSearchParams();

  const { locale: selectedLocale, title } = item;

  const localePrefix = getLocalePrefix(selectedLocale);

  const isSelected = selectedLocale === locale;

  const onLocaleChange = () => {
    startTransition(() => {
      const params = searchParams?.toString();

      if (state.alternates[selectedLocale]) {
        router.push(state.alternates[selectedLocale] + '?' + params);
      } else {
        const destinationPathname = removeLocaleFromPathname(pathname, locale);

        router.push(`${localePrefix}${destinationPathname}` + '?' + params);
      }
    });
  };

  return (
    <li>
      <button
        ref={buttonRef}
        disabled={isSelected}
        onClick={onLocaleChange}
        className={clsx(
          'listbox__option block',
          isSelected && 'font-bold pointer-events-none'
        )}
        data-testid={`alternate-page-switcher-item-${selectedLocale}`}
      >
        {title}
      </button>
    </li>
  );
};

export const AlternatePageSwitcher = ({
  locale,
  classNames,
}: AlternatePageSwitcherProps) => {
  return (
    <Suspense fallback={<></>}>
      <div
        data-testid='alternate-page-switcher'
        className={clsx('listbox group relative w-fit', classNames?.container)}
      >
        <IconButton
          tabIndex={-1}
          type='button'
          className='button flex items-center gap-1 duration-200 hover:opacity-70'
          iconProps={{
            icon: 'lang',
            className: classNames?.globeIcon,
            isInteractive: true,
          }}
        >
          <span className={clsx('text-base', classNames?.title)}>
            {langTitleDict[locale as Locales] || langTitleDict.en}
          </span>
          <Icons
            sizeClassName='w-6 h-6'
            icon='dropdown'
            isInteractive
            as='span'
            className={clsx('group-hover:rotate-180', classNames?.arrowIcon)}
          />
        </IconButton>
        <ul
          className={clsx(
            'listbox__options right-0 hidden group-hover:block group-focus:block',
            classNames?.options
          )}
        >
          {localesList.map((item) => (
            <AlternatePageSwitcherItem
              key={item.locale}
              item={item}
              locale={locale}
            />
          ))}
        </ul>
      </div>
    </Suspense>
  );
};

export default AlternatePageSwitcher;
